import { Tank } from "./Tank";

/**
 * Manages event listeners and their cleanup for the game
 */
export class EventManager {
  private static listeners: Map<string, { target: EventTarget; type: string; listener: EventListener; options?: boolean | AddEventListenerOptions }[]> = new Map();

  /**
   * Adds an event listener and tracks it for cleanup
   * @param id Unique identifier for the group of listeners (e.g., tank ID or component name)
   * @param target DOM element to attach the listener to
   * @param type Event type
   * @param listener Event listener function
   * @param options Event listener options
   */
  public static addListener(
    id: string,
    target: EventTarget,
    type: string,
    listener: EventListener,
    options?: boolean | AddEventListenerOptions
  ): void {
    if (!this.listeners.has(id)) {
      this.listeners.set(id, []);
    }

    this.listeners.get(id)?.push({ target, type, listener, options });
    target.addEventListener(type, listener, options);
  }

  /**
   * Removes all event listeners associated with the given ID
   * @param id Unique identifier for the group of listeners to remove
   */
  public static removeAllListeners(id: string): void {
    const listeners = this.listeners.get(id);
    if (listeners) {
      listeners.forEach(({ target, type, listener, options }) => {
        target.removeEventListener(type, listener, options);
      });
      this.listeners.delete(id);
      console.log(`[EventManager] Removed all listeners for ${id}`);
    }
  }

  /**
   * Removes a specific event listener
   * @param id Unique identifier for the group of listeners
   * @param target DOM element the listener is attached to
   * @param type Event type
   * @param listener Event listener function
   */
  public static removeListener(
    id: string,
    target: EventTarget,
    type: string,
    listener: EventListener
  ): void {
    const listeners = this.listeners.get(id);
    if (listeners) {
      const index = listeners.findIndex(
        (l) => l.target === target && l.type === type && l.listener === listener
      );
      if (index !== -1) {
        const { target, type, listener, options } = listeners[index];
        target.removeEventListener(type, listener, options);
        listeners.splice(index, 1);
        if (listeners.length === 0) {
          this.listeners.delete(id);
        }
      }
    }
  }
}